export interface RowTab {
  id: string;
  name: string;
  timeframes: Timeframe[];
  dataSource: DataSource;
  dataTypes: DataType[];
  coins: string[];
  dateFrom: string;
  dateTo: string;
  createdAt: string;
  updatedAt: string;
}

// prettier-ignore
export type Timeframe = '1m' | '5m' | '15m' | '30m' | '45m' | '1h' | '2h' | '3h' | '4h' | '6h' | '8h' | '12h' | '1d' | '2d' | '3d' | '1w' | '2w' | '1M';

export type DataSource = 'cleaned' | 'spikes';

export type DataType =
  | 'SPOT OHLCV'
  | 'FUTURES OHLCV'
  | 'Funding Rate'
  | 'Open Interest'
  | 'Global Long/Short Ratio'
  | 'Top Long/Short (Accounts)'
  | 'Top Long/Short (Positions)'
  | 'Taker Buy/Sell'
  | 'Spot Orderbook'
  | 'Futures Orderbook';

export const ALL_TIMEFRAMES: Timeframe[] = [
  '1m',
  '5m',
  '15m',
  '30m',
  '45m',
  '1h',
  '2h',
  '3h',
  '4h',
  '6h',
  '8h',
  '12h',
  '1d',
  '2d',
  '3d',
  '1w',
  '2w',
  '1M',
];

export interface DataTypeInfo {
  key: DataType;
  label: string;
  description: string;
  icon: string;
}

export const DATA_TYPE_DETAILS: DataTypeInfo[] = [
  {
    key: 'SPOT OHLCV',
    label: 'Spot OHLCV',
    description: 'Spot candles w/ taker_buy_volume',
    icon: '\uD83D\uDCCA',
  },
  {
    key: 'FUTURES OHLCV',
    label: 'Futures OHLCV',
    description: 'USDT-M Futures candles',
    icon: '\uD83D\uDCC8',
  },
  {
    key: 'Funding Rate',
    label: 'Funding Rate',
    description: 'Funding rate history',
    icon: '\uD83D\uDCB0',
  },
  {
    key: 'Open Interest',
    label: 'Open Interest',
    description: 'Open interest data',
    icon: '\uD83D\uDCC9',
  },
  {
    key: 'Global Long/Short Ratio',
    label: 'Global L/S Ratio',
    description: 'Global long/short ratio',
    icon: '\u2696\uFE0F',
  },
  {
    key: 'Top Long/Short (Accounts)',
    label: 'Top L/S (Accounts)',
    description: 'Top trader L/S by accounts',
    icon: '\uD83D\uDC64',
  },
  {
    key: 'Top Long/Short (Positions)',
    label: 'Top L/S (Positions)',
    description: 'Top trader L/S by positions',
    icon: '\uD83D\uDCCA',
  },
  {
    key: 'Taker Buy/Sell',
    label: 'Taker Buy/Sell',
    description: 'Taker buy/sell volume ratio',
    icon: '\uD83D\uDCA5',
  },
  {
    key: 'Spot Orderbook',
    label: 'Spot Orderbook',
    description: 'Spot depth / orderbook data',
    icon: '\uD83D\uDCD6',
  },
  {
    key: 'Futures Orderbook',
    label: 'Futures Orderbook',
    description: 'Futures depth / orderbook data',
    icon: '\uD83D\uDCD6',
  },
];

export const ALL_DATA_TYPES: DataType[] = DATA_TYPE_DETAILS.map((d) => d.key);

/**
 * Fallback coin list — used only when the CoinRegistryContext hasn't loaded yet.
 * The live list is fetched from ClickHouse via CoinRegistryContext.
 */
export const AVAILABLE_COINS_FALLBACK: string[] = [
  'AAVE',
  'ADA',
  'ARB',
  'AVAX',
  'BCH',
  'BNB',
  'BTC',
  'DOT',
  'ENA',
  'EOS',
  'ETC',
  'ETH',
  'FET',
  'GRT',
  'ICP',
  'IMX',
  'LDO',
  'LINK',
  'LTC',
  'MANTA',
  'MKR',
  'OP',
  'PENDLE',
  'RENDER',
  'SOL',
  'STX',
  'TAO',
  'TRX',
  'UNI',
  'WLD',
  'XLM',
  'XRP',
];

/** @deprecated Use useCoinRegistry().availableCoins instead. Kept for backwards compat. */
export const AVAILABLE_COINS = AVAILABLE_COINS_FALLBACK;

export const COIN_COLORS: Record<string, string> = {
  AAVE: '#B6509E',
  ADA: '#0033AD',
  ARB: '#28A0F0',
  AVAX: '#E84142',
  BCH: '#8DC351',
  BNB: '#F3BA2F',
  BTC: '#F7931A',
  DOT: '#E6007A',
  ENA: '#7B5BF0',
  EOS: '#443F54',
  ETC: '#328332',
  ETH: '#627EEA',
  FET: '#1D2951',
  GRT: '#6747ED',
  ICP: '#29ABE2',
  IMX: '#00BFFF',
  LDO: '#F69988',
  LINK: '#2A5ADA',
  LTC: '#345D9D',
  MANTA: '#1FC7D4',
  MKR: '#1AAB9B',
  OP: '#FF0420',
  PENDLE: '#EAFF00',
  RENDER: '#000000',
  SOL: '#9945FF',
  STX: '#5546FF',
  TAO: '#000000',
  TRX: '#FF0013',
  UNI: '#FF007A',
  WLD: '#000000',
  XLM: '#14B6E7',
  XRP: '#23292F',
};

/**
 * Fallback genesis dates — used only when the CoinRegistryContext hasn't loaded yet.
 * The live genesis is fetched from ClickHouse via CoinRegistryContext.
 */
export const COIN_GENESIS_FALLBACK: Record<string, string> = {
  AAVE: '2020-10-13',
  ADA: '2018-04-17',
  ARB: '2023-03-23',
  AVAX: '2020-09-22',
  BCH: '2018-01-12',
  BNB: '2017-11-06',
  BTC: '2017-08-17',
  DOT: '2020-08-19',
  ENA: '2024-04-02',
  EOS: '2018-05-31',
  ETC: '2018-06-12',
  ETH: '2017-08-17',
  FET: '2019-03-28',
  GRT: '2020-12-18',
  ICP: '2021-05-10',
  IMX: '2021-11-12',
  LDO: '2022-08-09',
  LINK: '2019-01-16',
  LTC: '2017-12-13',
  MANTA: '2024-01-18',
  MKR: '2020-06-10',
  OP: '2022-06-01',
  PENDLE: '2023-07-07',
  RENDER: '2023-11-27',
  SOL: '2020-08-11',
  STX: '2019-10-25',
  TAO: '2024-04-15',
  TRX: '2018-04-20',
  UNI: '2020-09-17',
  WLD: '2023-07-24',
  XLM: '2018-04-06',
  XRP: '2018-01-26',
};

/** @deprecated Use useCoinRegistry().coinGenesis instead. Kept for backwards compat. */
export const COIN_GENESIS = COIN_GENESIS_FALLBACK;

export const MAX_COINS = 5;

export interface ParquetFormState {
  name: string;
  timeframes: Timeframe[];
  dataSource: DataSource;
  dataTypes: DataType[];
  coins: string[];
  dateFrom: string;
  dateTo: string;
}

export interface ValidationErrors {
  name?: string;
  timeframes?: string;
  dataTypes?: string;
  coins?: string;
  dateRange?: string;
}

export const EMPTY_FORM: ParquetFormState = {
  name: '',
  timeframes: [],
  dataSource: 'spikes',
  dataTypes: [],
  coins: [],
  dateFrom: '',
  dateTo: '',
};
